import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, type AuthTokens, type Role, api, setSession } from './api';
import { AUTH_BUTTON, AUTH_INPUT, AuthField, AuthShell } from './components/AuthShell';
import { PasswordInput } from './components/ui/Input';
import { type Lang, setLang, t } from './i18n';

/**
 * One sign-in screen for the whole family: husband (boss), wife (boss),
 * housekeeper (staff), nanny (staff). The form takes a single identifier
 * (email *or* username) plus password; the backend figures out the role.
 *
 * If the same username exists in multiple households, the server returns
 * AMBIGUOUS_USERNAME and we re-prompt with a household-code field.
 */
export function Signin() {
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [householdCode, setHouseholdCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [needsChange, setNeedsChange] = useState(false);
  const [role, setRole] = useState<Role>('staff');
  const [newPassword, setNewPassword] = useState('');

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      // Session is already set, so the call carries the fresh access token.
      // Server returns 204 and the token stays valid.
      await api('/v1/auth/staff/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: password, new_password: newPassword }),
      });
      nav(role === 'owner' ? '/home' : '/today');
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const body: Record<string, string> = {
        identifier: identifier.trim(),
        password,
      };
      if (needCode && householdCode.trim()) body.household_code = householdCode.trim();

      const res = await api<{
        tokens: AuthTokens;
        user: { preferred_lang: Lang; role: Role };
        must_change_password: boolean;
      }>('/v1/auth/signin', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setSession(res.tokens, res.user.role);
      setLang(res.user.preferred_lang);

      // A staff member's first password is one the owner typed for them and
      // handed over out loud or on paper. Until it's replaced, anyone who saw
      // it can sign in as them. The server flags this; we must act on it —
      // this screen used to read the flag and navigate straight past it.
      if (res.must_change_password) {
        setRole(res.user.role);
        setNeedsChange(true);
        return;
      }

      // Role-aware landing: bosses → dashboard, staff → today.
      nav(res.user.role === 'owner' ? '/home' : '/today');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'AMBIGUOUS_USERNAME') {
        setNeedCode(true);
        setErr(
          t('This username exists in more than one household. Add the household code to continue.'),
        );
      } else {
        setErr(e instanceof ApiError ? e.message : t('Failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (needsChange) {
    return (
      <AuthShell
        title={t('One more thing')}
        subtitle={t('Only you should know it — not the person who set up your account.')}
      >
        <form onSubmit={changePassword} className="space-y-4">
          <AuthField label={t('New password')} htmlFor="signin-new-password">
            <input
              id="signin-new-password"
              className={AUTH_INPUT}
              type="password"
              placeholder="••••••••••"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={10}
              autoComplete="new-password"
              // biome-ignore lint/a11y/noAutofocus: the reset step is a full-screen form whose only field is this one; focusing it saves a tap on mobile, which is the deliberate flow here
              autoFocus
              required
            />
          </AuthField>
          {err && <div className="text-sm font-semibold text-urgent">{err}</div>}
          <button type="submit" className={AUTH_BUTTON} disabled={busy}>
            {busy ? '…' : t('Continue')}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="famanage">
      <form onSubmit={submit} className="space-y-4">
        <AuthField label={t('Email or username')} htmlFor="signin-identifier">
          <input
            id="signin-identifier"
            className={AUTH_INPUT}
            dir="ltr"
            placeholder={t('Enter details')}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            // biome-ignore lint/a11y/noAutofocus: sign-in is a dedicated full-screen route and this is its first field; opening ready to type is the intended mobile-first flow
            autoFocus
            autoComplete="username"
            required
          />
        </AuthField>
        <AuthField label={t('Password')} htmlFor="signin-password">
          <PasswordInput
            id="signin-password"
            bare
            className={AUTH_INPUT}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </AuthField>
        {needCode && (
          <AuthField label={t('Household code')} htmlFor="signin-household-code">
            <input
              id="signin-household-code"
              className={AUTH_INPUT}
              dir="ltr"
              value={householdCode}
              onChange={(e) => setHouseholdCode(e.target.value.toUpperCase())}
              required
            />
          </AuthField>
        )}
        {err && <div className="text-sm font-semibold text-urgent">{err}</div>}
        <button type="submit" className={AUTH_BUTTON} disabled={busy}>
          {busy ? '…' : t('Continue')}
        </button>
      </form>

      {/* His mock offers Google and Apple here. Neither is built, and a button
          that opens nothing on a sign-in screen is the worst place in an app
          to put one. The real other path is starting a household. */}
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
          {t('New?')}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <Link
        to="/signup"
        className="flex h-16 w-full items-center justify-center rounded-2xl border border-border bg-card text-sm font-bold transition-colors active:bg-secondary"
      >
        {t('Start your household')}
      </Link>
    </AuthShell>
  );
}
